import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { DragEndEvent, DragOverEvent, UniqueIdentifier } from '@dnd-kit/core';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BFButton from '../button/BFButton';
import './BFTable.css';

// ============ TYPES ============

export type SortDirection = 'asc' | 'desc' | null;

export type FilterType = 'contains' | 'startsWith';

export type CellType = 'text' | 'link' | 'actions' | 'checkbox' | 'custom';

export interface BFTableAction {
    /** Unique identifier for the action */
    key: string;
    /** Icon component or element to render */
    icon: React.ReactNode;
    /** Tooltip text */
    tooltip?: string;
    /** Click handler for the action */
    onClick?: (record: Record<string, unknown>, rowIndex: number) => void;
    /** Whether the action is disabled */
    disabled?: boolean | ((record: Record<string, unknown>) => boolean);
    /** Whether the action is hidden */
    hidden?: boolean | ((record: Record<string, unknown>) => boolean);
}

export interface BFTableFilterValue {
    type: FilterType;
    value: string;
}

export interface BFTableColumn<T = Record<string, unknown>> {
    /** Unique key for the column */
    key: string;
    /** Data index - property name in the data object */
    dataIndex: string;
    /** Column header title */
    title: string;
    /** Column width (px or %) */
    width?: number | string;
    /** Minimum width */
    minWidth?: number;
    /** Cell type */
    type?: CellType;
    /** Whether the column is sortable */
    sortable?: boolean;
    /** Whether the column has filter options */
    filterable?: boolean;
    /** Custom render function for cell content */
    render?: (value: unknown, record: T, rowIndex: number) => React.ReactNode;
    /** Actions configuration (when type is 'actions') */
    actions?: BFTableAction[];
    /** Link configuration (when type is 'link') */
    linkConfig?: {
        /** URL or function to generate URL */
        href: string | ((record: T) => string);
        /** Whether to open in new tab */
        target?: '_blank' | '_self';
    };
    /** Text alignment */
    align?: 'left' | 'center' | 'right';
    /** Whether the column is fixed */
    fixed?: 'left' | 'right';
    /** Custom class name for the column */
    className?: string;
}

export interface SortConfig {
    key: string;
    direction: SortDirection;
}

export interface FilterConfig {
    [columnKey: string]: BFTableFilterValue;
}

export interface PaginationConfig {
    /** Current page number (1-indexed) */
    currentPage: number;
    /** Number of items per page */
    pageSize: number;
    /** Total number of items */
    total: number;
    /** Available page size options */
    pageSizeOptions?: number[];
}

export interface LoadMoreConfig {
    /** Number of items to load per click */
    pageSize: number;
    /** Total number of items available */
    total: number;
    /** Number of items currently loaded */
    loadedCount: number;
}

export interface BFTableProps<T = Record<string, unknown>> {
    /** Column definitions */
    columns: BFTableColumn<T>[];
    /** Data source array */
    dataSource: T[];
    /** Unique key property name in data */
    rowKey?: string | ((record: T) => string);
    /** Whether columns can be dragged to reorder */
    draggable?: boolean;
    /** Callback when columns are reordered */
    onColumnsReorder?: (newColumns: BFTableColumn<T>[]) => void;
    /** Currently active/selected row key (controlled mode) */
    activeRowKey?: string | number | null;
    /** Callback when active row changes */
    onActiveRowChange?: (key: string | number | null, record: T | null) => void;
    /** Callback when a row is clicked */
    onRowClick?: (record: T, rowIndex: number) => void;
    /** Callback when a row is double-clicked */
    onRowDoubleClick?: (record: T, rowIndex: number) => void;
    /** Current sort configuration (controlled mode) */
    sortConfig?: SortConfig | null;
    /**
     * Callback when sort changes. If provided, sorting will be handled externally (BE).
     * Table will show loading state until new data arrives.
     */
    onSort?: (config: SortConfig | null) => Promise<void> | void;
    /** Current filter configuration (controlled mode) */
    filterConfig?: FilterConfig;
    /**
     * Callback when filter changes. If provided, filtering will be handled externally (BE).
     * Table will show loading state until new data arrives.
     */
    onFilterSet?: (config: FilterConfig) => Promise<void> | void;
    /** Whether to show row selection checkboxes */
    selectable?: boolean;
    /** Selected row keys */
    selectedRowKeys?: (string | number)[];
    /** Callback when selection changes */
    onSelectionChange?: (selectedKeys: (string | number)[], selectedRecords: T[]) => void;
    /** Loading state */
    loading?: boolean;
    /** Empty state message */
    emptyText?: string;
    /** Table size variant */
    size?: 'small' | 'medium' | 'large';
    /** Additional class name */
    className?: string;
    /** Whether rows are clickable (shows hover effect) */
    clickable?: boolean;
    /** Whether to show borders */
    bordered?: boolean;
    /** Sticky header */
    stickyHeader?: boolean;
    /** Max height for scrollable body */
    maxHeight?: number | string;
    maxWidth?: number | string;
    /** Pagination configuration (controlled mode) */
    pagination?: PaginationConfig;
    /** Callback when pagination changes */
    onPaginationChange?: (currentPage: number, pageSize: number) => void;
    /** Load more configuration (controlled mode) */
    loadMore?: LoadMoreConfig;
    /** Callback when load more is triggered. If provided, loading will be handled externally (BE). */
    onLoadMore?: () => Promise<void> | void;
    /** Whether load more button is loading */
    loadMoreLoading?: boolean;
    /** When true, skips client-side pagination slicing (server already returns the correct page) */
    serverSide?: boolean;
}

// ============ CONTEXT ============

interface DragIndexState {
    active: UniqueIdentifier;
    over: UniqueIdentifier | undefined;
    direction?: 'left' | 'right';
}

const DragIndexContext = createContext<DragIndexState>({ active: -1, over: -1 });

// ============ HELPER COMPONENTS ============

interface SortableHeaderCellProps {
    id: string;
    children: React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
    draggable?: boolean;
}

function SortableHeaderCell({ id, children, style, className, draggable }: SortableHeaderCellProps) {
    const dragState = useContext(DragIndexContext);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const dragStyle: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999, opacity: 0.8 } : {}),
    };

    // Apply drag active styling
    const getDragActiveStyle = (): React.CSSProperties => {
        const { active, over, direction } = dragState;
        if (active && active === id) {
            return { backgroundColor: 'var(--bf-grey-deep-dark)', opacity: 0.5 };
        }
        if (over && id === over && active !== over) {
            return direction === 'right'
                ? { borderRight: '2px dashed var(--bf-logo-gold)' }
                : { borderLeft: '2px dashed var(--bf-logo-gold)' };
        }
        return {};
    };

    if (!draggable) {
        return (
            <th className={className} style={style}>
                {children}
            </th>
        );
    }

    return (
        <th
            ref={setNodeRef}
            style={{ ...style, ...dragStyle, ...getDragActiveStyle() }}
            className={`${className || ''} bf-table-draggable-header`}
            {...attributes}
            {...listeners}
        >
            {children}
        </th>
    );
}

interface FilterPopupProps {
    columnKey: string;
    columnTitle: string;
    currentFilter?: BFTableFilterValue;
    onApply: (columnKey: string, filter: BFTableFilterValue | null) => void;
    onClose: () => void;
    anchorEl: HTMLElement | null;
}

function FilterPopup({ columnKey, columnTitle, currentFilter, onApply, onClose, anchorEl }: FilterPopupProps) {
    const [filterType, setFilterType] = useState<FilterType>(currentFilter?.type || 'contains');
    const [filterValue, setFilterValue] = useState(currentFilter?.value || '');
    const popupRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Close on any scroll or resize (to avoid mispositioned popup while scrolling)
    useEffect(() => {
        const closeOnScroll = () => onClose();
        window.addEventListener('scroll', closeOnScroll, true);
        window.addEventListener('resize', closeOnScroll);
        return () => {
            window.removeEventListener('scroll', closeOnScroll, true);
            window.removeEventListener('resize', closeOnScroll);
        };
    }, [onClose]);

    // Position relative to anchor element using fixed coordinates
    useEffect(() => {
        if (!anchorEl) return;
        const compute = () => {
            const rect = anchorEl.getBoundingClientRect();
            const gap = 6;
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;

            // Default size assumptions before measuring
            let popupW = popupRef.current?.offsetWidth || 240;
            let popupH = popupRef.current?.offsetHeight || 160;

            // Place below by default
            let top = rect.bottom + gap;
            let left = rect.right - popupW; // right-align with button

            // Measure actual popup after it's rendered
            if (popupRef.current) {
                popupW = popupRef.current.offsetWidth;
                popupH = popupRef.current.offsetHeight;
                left = rect.right - popupW;
            }

            // Keep within viewport horizontally
            if (left < 8) left = 8;
            if (left + popupW > viewportW - 8) left = Math.max(8, viewportW - popupW - 8);

            // If not enough space below, flip above
            if (top + popupH > viewportH - 8) {
                top = rect.top - gap - popupH;
            }

            // Final safety clamp
            if (top < 8) top = 8;

            setPosition({ top, left });
        };

        // Compute position on next frame for accurate sizes
        const raf = requestAnimationFrame(compute);
        return () => cancelAnimationFrame(raf);
    }, [anchorEl]);

    const handleApply = () => {
        if (filterValue.trim()) {
            onApply(columnKey, { type: filterType, value: filterValue.trim() });
        } else {
            onApply(columnKey, null);
        }
        onClose();
    };

    const handleClear = () => {
        onApply(columnKey, null);
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleApply();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const popup = (
        <div
            className="bf-table-filter-popup"
            ref={popupRef}
            style={position ? { position: 'fixed', top: position.top, left: position.left } : undefined}
        >
            <div className="bf-table-filter-popup-header">
                <span>Filter: {columnTitle}</span>
            </div>
            <div className="bf-table-filter-popup-body">
                <div className="bf-table-filter-type-select">
                    <button
                        type="button"
                        className={`bf-table-filter-type-btn ${filterType === 'contains' ? 'active' : ''}`}
                        onClick={() => setFilterType('contains')}
                    >
                        Contains
                    </button>
                    <button
                        type="button"
                        className={`bf-table-filter-type-btn ${filterType === 'startsWith' ? 'active' : ''}`}
                        onClick={() => setFilterType('startsWith')}
                    >
                        Starts with
                    </button>
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    className="bf-table-filter-input"
                    placeholder="Enter filter value..."
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </div>
            <div className="bf-table-filter-popup-footer">
                <button type="button" className="bf-table-filter-clear-btn" onClick={handleClear}>
                    Clear
                </button>
                <button type="button" className="bf-table-filter-apply-btn" onClick={handleApply}>
                    Apply
                </button>
            </div>
        </div>
    );

    return createPortal(popup, document.body);
}

// ============ ICONS ============

function SortAscIcon({ active }: { active?: boolean }) {
    return (
        <svg
            className={`bf-table-sort-icon ${active ? 'active' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
            width="14"
            height="14"
        >
            <path d="M7 14l5-5 5 5H7z" />
        </svg>
    );
}

function SortDescIcon({ active }: { active?: boolean }) {
    return (
        <svg
            className={`bf-table-sort-icon ${active ? 'active' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
            width="14"
            height="14"
        >
            <path d="M7 10l5 5 5-5H7z" />
        </svg>
    );
}

function FilterIcon({ active }: { active?: boolean }) {
    return (
        <svg
            className={`bf-table-filter-icon ${active ? 'active' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
            width="14"
            height="14"
        >
            <circle cx="12" cy="5" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
        </svg>
    );
}

function DragHandleIcon() {
    return (
        <svg className="bf-table-drag-icon" viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
            <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
        </svg>
    );
}

function LoadingSpinner() {
    return (
        <div className="bf-table-loading">
            <div className="bf-table-spinner" />
        </div>
    );
}

interface PaginationControlsProps {
    config: PaginationConfig;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
}

function PaginationControls({ config, onPageChange, onPageSizeChange }: PaginationControlsProps) {
    const { currentPage, pageSize, total } = config;
    const pageSizeOptions = config.pageSizeOptions || [10, 25, 50, 100];
    const totalPages = Math.ceil(total / pageSize);

    // Generate page numbers to display
    const getPageNumbers = (): (number | string)[] => {
        const maxPagesToShow = 4;

        if (totalPages <= maxPagesToShow) {
            const result: (number | string)[] = [];
            for (let i = 1; i <= totalPages; i += 1) {
                result.push(i);
            }
            return result;
        }

        if (currentPage <= 3) {
            const result: (number | string)[] = [];
            for (let i = 1; i <= 4; i += 1) {
                result.push(i);
            }
            result.push('...');
            result.push(totalPages);
            return result;
        }

        if (currentPage >= totalPages - 2) {
            return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }

        // Middle: 1, ..., prev, current, next, [...,] last
        const result: (number | string)[] = [1, '...', currentPage - 1, currentPage, currentPage + 1];
        if (currentPage + 1 < totalPages - 1) {
            result.push('...');
        }
        result.push(totalPages);
        return result;
    };

    return (
        <div className="bf-table-pagination">
            <div className="bf-pagination-section">
                <div className="bf-table-pagination-left">
                    <div style={{ visibility: currentPage > 1 ? 'visible' : 'hidden', display: 'inline-flex', gap: 4 }}>
                        <button
                            type="button"
                            className="bf-table-pagination-btn bf-table-pagination-btn-icon"
                            onClick={() => onPageChange(1)}
                            title="First page"
                            aria-label="First page"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 6h2v12H6zM15.5 6L7 12l8.5 6z" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="bf-table-pagination-btn bf-table-pagination-btn-icon"
                            onClick={() => onPageChange(currentPage - 1)}
                            title="Previous page"
                            aria-label="Previous page"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="bf-table-pagination-pages">
                    {getPageNumbers().map((page, i) => {
                        if (page === '...') {
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <span key={`page-ellipsis-${i}`} className="bf-table-pagination-ellipsis">
                                    ...
                                </span>
                            );
                        }

                        return (
                            <button
                                key={`page-${page}`}
                                type="button"
                                className={`bf-table-pagination-number ${currentPage === page ? 'active' : ''}`}
                                onClick={() => onPageChange(page as number)}
                            >
                                {page}
                            </button>
                        );
                    })}
                </div>

                <div className="bf-table-pagination-right">
                    {currentPage < totalPages && (
                        <>
                            <button
                                type="button"
                                className="bf-table-pagination-btn bf-table-pagination-btn-icon"
                                onClick={() => onPageChange(currentPage + 1)}
                                title="Next page"
                                aria-label="Next page"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="bf-table-pagination-btn bf-table-pagination-btn-icon"
                                onClick={() => onPageChange(totalPages)}
                                title="Last page"
                                aria-label="Last page"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M16 6h2v12h-2zM8.5 6l8.5 6-8.5 6z" />
                                </svg>
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="bf-table-pagination-items-per-page">
                <span>Items per page</span>
                <div className="bf-table-pagination-select-wrapper">
                    <select
                        id="bf-pagination-page-size"
                        className="bf-table-pagination-select"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        aria-label="Items per page"
                    >
                        {pageSizeOptions.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                    <svg className="bf-table-pagination-select-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 10l5 5 5-5H7z" />
                    </svg>
                </div>
            </div>
        </div>
    );
}

interface LoadMoreControlsProps {
    config: LoadMoreConfig;
    onLoadMore: () => void;
    loading?: boolean;
}

function LoadMoreControls({ config, onLoadMore, loading = false }: LoadMoreControlsProps) {
    const { loadedCount, total } = config;
    const hasMore = loadedCount < total;
    const remainingCount = total - loadedCount;

    if (!hasMore) {
        return null;
    }

    return (
        <div className="bf-table-load-more">
            <BFButton
                onClick={onLoadMore}
                disabled={loading}
                loading={loading}
                color="primary"
                size="normal"
                borderRadius="rounded"
            >
                Load More ({remainingCount} remaining)
            </BFButton>
        </div>
    );
}

// ============ MAIN COMPONENT ============

export function BFTable<T extends Record<string, unknown>>({
    columns: initialColumns,
    dataSource,
    rowKey = 'id',
    draggable = false,
    onColumnsReorder,
    activeRowKey: controlledActiveRowKey,
    onActiveRowChange,
    onRowClick,
    onRowDoubleClick,
    sortConfig: controlledSortConfig,
    onSort,
    filterConfig: controlledFilterConfig,
    onFilterSet,
    selectable = false,
    selectedRowKeys: controlledSelectedRowKeys,
    onSelectionChange,
    loading: externalLoading = false,
    emptyText = 'No data available',
    size = 'medium',
    className = '',
    clickable = true,
    bordered = false,
    stickyHeader = false,
    maxHeight,
    maxWidth,
    pagination,
    onPaginationChange,
    loadMore,
    onLoadMore,
    loadMoreLoading = false,
    serverSide = false,
}: BFTableProps<T>) {
    // State
    const [columns, setColumns] = useState(initialColumns);
    const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
    const [dragIndex, setDragIndex] = useState<DragIndexState>({ active: -1, over: -1 });
    const [internalLoading, setInternalLoading] = useState(false);

    // Internal state for uncontrolled mode
    const [internalActiveRowKey, setInternalActiveRowKey] = useState<string | number | null>(null);
    const [internalSortConfig, setInternalSortConfig] = useState<SortConfig | null>(null);
    const [internalFilterConfig, setInternalFilterConfig] = useState<FilterConfig>({});
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<(string | number)[]>([]);
    const [internalPagination, setInternalPagination] = useState<PaginationConfig | null>(null);
    const [internalLoadMoreLoading, setInternalLoadMoreLoading] = useState(false);

    // Determine if we're in controlled mode
    const isActiveRowControlled = controlledActiveRowKey !== undefined;
    const isSortControlled = controlledSortConfig !== undefined;
    const isFilterControlled = controlledFilterConfig !== undefined;
    const isSelectionControlled = controlledSelectedRowKeys !== undefined;
    const isPaginationControlled = pagination !== undefined;

    // Get current values (controlled or internal)
    const activeRowKey = isActiveRowControlled ? controlledActiveRowKey : internalActiveRowKey;
    const sortConfig = isSortControlled ? controlledSortConfig : internalSortConfig;
    const filterConfig = isFilterControlled ? controlledFilterConfig : internalFilterConfig;
    const selectionKeys = isSelectionControlled ? controlledSelectedRowKeys || [] : internalSelectedRowKeys;
    const currentPagination = isPaginationControlled ? pagination : internalPagination;

    const loading = externalLoading || internalLoading;

    // Update columns when initialColumns change
    useEffect(() => {
        setColumns(initialColumns);
    }, [initialColumns]);

    // Get row key value
    const getRowKey = useCallback(
        (record: T, index: number): string | number => {
            if (typeof rowKey === 'function') {
                return rowKey(record);
            }
            return (record[rowKey] as string | number) ?? index;
        },
        [rowKey],
    );

    // Apply local sorting and filtering
    const processedData = useMemo(() => {
        let result = [...dataSource];

        // Apply local filtering (only if no external handler)
        if (!onFilterSet && filterConfig) {
            Object.entries(filterConfig).forEach(([columnKey, filter]) => {
                if (filter && filter.value) {
                    result = result.filter((record) => {
                        const column = columns.find((col) => col.key === columnKey);
                        if (!column) return true;

                        const value = String(record[column.dataIndex] ?? '').toLowerCase();
                        const filterVal = filter.value.toLowerCase();

                        if (filter.type === 'contains') {
                            return value.includes(filterVal);
                        }
                        // startsWith
                        return value.startsWith(filterVal);
                    });
                }
            });
        }

        // Apply local sorting (only if no external handler)
        if (!onSort && sortConfig && sortConfig.direction) {
            const column = columns.find((col) => col.key === sortConfig.key);
            if (column) {
                result.sort((a, b) => {
                    const aVal = a[column.dataIndex];
                    const bVal = b[column.dataIndex];

                    // Handle null/undefined
                    if (aVal == null && bVal == null) return 0;
                    if (aVal == null) return 1;
                    if (bVal == null) return -1;

                    // Compare values
                    let comparison = 0;
                    if (typeof aVal === 'number' && typeof bVal === 'number') {
                        comparison = aVal - bVal;
                    } else {
                        comparison = String(aVal).localeCompare(String(bVal));
                    }

                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                });
            }
        }

        // Apply pagination if enabled (skip when server already returns the correct page)
        if (currentPagination && !serverSide) {
            const { currentPage, pageSize } = currentPagination;
            const startIndex = (currentPage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            result = result.slice(startIndex, endIndex);
        }

        return result;
    }, [dataSource, sortConfig, filterConfig, columns, onSort, onFilterSet, currentPagination]);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    // Handle column reorder
    const handleDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            if (active.id !== over?.id) {
                setColumns((prevColumns) => {
                    const activeIndex = prevColumns.findIndex((col) => col.key === active.id);
                    const overIndex = prevColumns.findIndex((col) => col.key === over?.id);
                    const newColumns = arrayMove(prevColumns, activeIndex, overIndex);
                    onColumnsReorder?.(newColumns);
                    return newColumns;
                });
            }
            setDragIndex({ active: -1, over: -1 });
        },
        [onColumnsReorder],
    );

    const handleDragOver = useCallback(
        ({ active, over }: DragOverEvent) => {
            const activeIndex = columns.findIndex((col) => col.key === active.id);
            const overIndex = columns.findIndex((col) => col.key === over?.id);
            setDragIndex({
                active: active.id,
                over: over?.id,
                direction: overIndex !== undefined && overIndex > activeIndex ? 'right' : 'left',
            });
        },
        [columns],
    );

    // Handle row click - toggle active or selection according to rules
    const handleRowClick = useCallback(
        (record: T, rowIndex: number) => {
            const key = getRowKey(record, rowIndex);
            const isActive = activeRowKey === key;
            const isSelected = selectionKeys.includes(key);

            // If an external onRowClick handler is provided, delegate fully to it
            // and skip internal selection toggling so row-body click ≠ checkbox click.
            if (onRowClick) {
                onRowClick(record, rowIndex);
                return;
            }

            if (selectable) {
                // If row is active and clicked, set inactive
                if (isActive && !isActiveRowControlled) {
                    setInternalActiveRowKey(null);
                }
                onActiveRowChange?.(isActive ? null : activeRowKey ?? null, isActive ? null : null);

                if (isSelected) {
                    // Selected + click => unselect only
                    const newKeys = selectionKeys.filter((k) => k !== key);
                    const newRecords = processedData.filter((r, i) => newKeys.includes(getRowKey(r, i)));
                    if (isSelectionControlled) {
                        onSelectionChange?.(newKeys, newRecords);
                    } else {
                        setInternalSelectedRowKeys(newKeys);
                    }
                } else if (!isActive) {
                    // Not active + selectable + click => select (keep inactive)
                    const newKeys = [...selectionKeys, key];
                    const newRecords = processedData.filter((r, i) => newKeys.includes(getRowKey(r, i)));
                    if (isSelectionControlled) {
                        onSelectionChange?.(newKeys, newRecords);
                    } else {
                        setInternalSelectedRowKeys(newKeys);
                    }
                }
                return;
            }

            // Not selectable: toggle active
            if (!isActiveRowControlled) {
                setInternalActiveRowKey(isActive ? null : key);
            }
            onActiveRowChange?.(isActive ? null : key, isActive ? null : record);
        },
        [
            getRowKey,
            activeRowKey,
            isActiveRowControlled,
            onActiveRowChange,
            onRowClick,
            selectable,
            selectionKeys,
            isSelectionControlled,
            onSelectionChange,
            processedData,
        ],
    );

    // Handle sort
    const handleSort = useCallback(
        async (columnKey: string) => {
            // Calculate new sort direction: asc -> desc -> null -> asc
            let newDirection: SortDirection = 'asc';
            if (sortConfig?.key === columnKey) {
                if (sortConfig.direction === 'asc') {
                    newDirection = 'desc';
                } else if (sortConfig.direction === 'desc') {
                    newDirection = null;
                }
            }

            const newConfig: SortConfig | null = newDirection ? { key: columnKey, direction: newDirection } : null;

            // If external handler exists, call it with loading state
            if (onSort) {
                setInternalLoading(true);
                try {
                    await onSort(newConfig);
                } finally {
                    setInternalLoading(false);
                }
            } else {
                // Local sorting - just update internal state
                setInternalSortConfig(newConfig);
            }
        },
        [sortConfig, onSort],
    );

    // Handle filter
    const handleFilter = useCallback(
        async (columnKey: string, filter: BFTableFilterValue | null) => {
            const newConfig = { ...filterConfig };

            if (filter) {
                newConfig[columnKey] = filter;
            } else {
                delete newConfig[columnKey];
            }

            // If external handler exists, call it with loading state
            if (onFilterSet) {
                setInternalLoading(true);
                try {
                    await onFilterSet(newConfig);
                } finally {
                    setInternalLoading(false);
                }
            } else {
                // Local filtering - just update internal state
                setInternalFilterConfig(newConfig);
            }
        },
        [filterConfig, onFilterSet],
    );

    // Handle pagination
    const handlePaginationChange = useCallback(
        (newPage: number, newPageSize: number) => {
            if (onPaginationChange) {
                onPaginationChange(newPage, newPageSize);
            } else if (!isPaginationControlled) {
                // Update internal pagination state
                setInternalPagination({
                    currentPage: newPage,
                    pageSize: newPageSize,
                    total: dataSource.length,
                });
            }
        },
        [onPaginationChange, isPaginationControlled, dataSource.length],
    );

    // Handle load more
    const handleLoadMore = useCallback(async () => {
        if (onLoadMore) {
            setInternalLoadMoreLoading(true);
            try {
                await Promise.resolve(onLoadMore());
            } finally {
                setInternalLoadMoreLoading(false);
            }
        }
    }, [onLoadMore]);

    const currentLoadMoreLoading = loadMoreLoading || internalLoadMoreLoading;

    // Handle row selection
    const handleSelectAll = useCallback(() => {
        const allKeys = processedData.map((record, index) => getRowKey(record, index));
        const allSelected = allKeys.every((key) => selectionKeys.includes(key));

        if (allSelected) {
            if (isSelectionControlled) {
                onSelectionChange?.([], []);
            } else {
                setInternalSelectedRowKeys([]);
            }
        } else if (isSelectionControlled) {
            onSelectionChange?.(allKeys, [...processedData]);
        } else {
            setInternalSelectedRowKeys(allKeys);
        }
    }, [processedData, selectionKeys, isSelectionControlled, onSelectionChange, getRowKey]);

    const handleSelectRow = useCallback(
        (record: T, index: number) => {
            const key = getRowKey(record, index);
            const isSelected = selectionKeys.includes(key);

            if (isSelected) {
                const newKeys = selectionKeys.filter((k) => k !== key);
                const newRecords = processedData.filter((r, i) => newKeys.includes(getRowKey(r, i)));
                if (isSelectionControlled) {
                    onSelectionChange?.(newKeys, newRecords);
                } else {
                    setInternalSelectedRowKeys(newKeys);
                }
            } else {
                const newKeys = [...selectionKeys, key];
                const newRecords = processedData.filter((r, i) => newKeys.includes(getRowKey(r, i)));
                if (isSelectionControlled) {
                    onSelectionChange?.(newKeys, newRecords);
                } else {
                    setInternalSelectedRowKeys(newKeys);
                }
            }
        },
        [selectionKeys, isSelectionControlled, onSelectionChange, processedData, getRowKey],
    );
    const renderCellContent = useCallback((column: BFTableColumn<T>, record: T, rowIndex: number) => {
        const value = record[column.dataIndex];

        // Custom render function takes priority
        if (column.render) {
            return column.render(value, record, rowIndex);
        }

        switch (column.type) {
            case 'link':
                if (column.linkConfig) {
                    const href =
                        typeof column.linkConfig.href === 'function'
                            ? column.linkConfig.href(record)
                            : column.linkConfig.href;
                    return (
                        <a
                            href={href}
                            target={column.linkConfig.target || '_self'}
                            className="bf-table-link"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {String(value ?? '')}
                        </a>
                    );
                }
                return String(value ?? '');

            case 'actions':
                return (
                    <div className="bf-table-actions">
                        {column.actions?.map((action) => {
                            const isDisabled =
                                typeof action.disabled === 'function'
                                    ? action.disabled(record as Record<string, unknown>)
                                    : action.disabled;
                            const isHidden =
                                typeof action.hidden === 'function'
                                    ? action.hidden(record as Record<string, unknown>)
                                    : action.hidden;

                            if (isHidden) return null;

                            return (
                                <button
                                    key={action.key}
                                    type="button"
                                    className={`bf-table-action-btn ${isDisabled ? 'disabled' : ''}`}
                                    title={action.tooltip}
                                    disabled={isDisabled}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        action.onClick?.(record as Record<string, unknown>, rowIndex);
                                    }}
                                >
                                    {action.icon}
                                </button>
                            );
                        })}
                    </div>
                );

            case 'checkbox':
                return (
                    <input
                        type="checkbox"
                        checked={Boolean(value)}
                        readOnly
                        className="bf-table-checkbox"
                        onClick={(e) => e.stopPropagation()}
                    />
                );

            case 'text':
            default:
                return <span className="bf-table-text">{String(value ?? '')}</span>;
        }
    }, []);

    // Render header cell
    const renderHeaderCell = useCallback(
        (column: BFTableColumn<T>) => {
            const isSorted = sortConfig?.key === column.key;
            const isAsc = isSorted && sortConfig?.direction === 'asc';
            const isDesc = isSorted && sortConfig?.direction === 'desc';
            const hasActiveFilter = filterConfig && filterConfig[column.key]?.value;

            return (
                <div className="bf-table-header-content">
                    {draggable && <DragHandleIcon />}
                    <span className={`bf-table-header-title${hasActiveFilter ? ' bf-table-header-title--filtered' : ''}`}>{column.title}</span>
                    {column.sortable && (
                        <button
                            type="button"
                            className={`bf-table-sort-btn ${isSorted ? 'active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSort(column.key);
                            }}
                        >
                            <div className="bf-table-sort-icons">
                                <SortAscIcon active={isAsc} />
                                <SortDescIcon active={isDesc} />
                            </div>
                        </button>
                    )}
                    {column.filterable && (
                        <div className="bf-table-filter-wrapper">
                            <button
                                type="button"
                                className={`bf-table-filter-btn ${
                                    openFilterKey === column.key || hasActiveFilter ? 'active' : ''
                                }`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const nextOpen = openFilterKey === column.key ? null : column.key;
                                    setOpenFilterKey(nextOpen);
                                    setFilterAnchorEl(nextOpen ? (e.currentTarget as HTMLElement) : null);
                                }}
                            >
                                <FilterIcon active={Boolean(hasActiveFilter)} />
                            </button>
                            {openFilterKey === column.key && (
                                <FilterPopup
                                    columnKey={column.key}
                                    columnTitle={column.title}
                                    currentFilter={filterConfig?.[column.key]}
                                    onApply={handleFilter}
                                    onClose={() => {
                                        setOpenFilterKey(null);
                                        setFilterAnchorEl(null);
                                    }}
                                    anchorEl={filterAnchorEl}
                                />
                            )}
                        </div>
                    )}
                </div>
            );
        },
        [draggable, sortConfig, filterConfig, openFilterKey, handleSort, handleFilter, filterAnchorEl],
    );

    // Compute table class names
    const tableClassName = useMemo(() => {
        const classes = ['bf-table', `bf-table-${size}`];
        if (bordered) classes.push('bf-table-bordered');
        if (stickyHeader) classes.push('bf-table-sticky-header');
        if (className) classes.push(className);
        return classes.join(' ');
    }, [size, bordered, stickyHeader, className]);

    // Render table content
    const tableContent = (
        <div className="bf-table-container">
            <div
                className="bf-table-wrapper"
                style={
                    maxHeight || maxWidth
                        ? {
                              maxHeight,
                              maxWidth,
                              overflowY: maxHeight ? 'auto' : undefined,
                              overflowX: maxWidth ? 'auto' : undefined,
                          }
                        : undefined
                }
            >
                {loading && <LoadingSpinner />}
                <table className={tableClassName} style={{ visibility: loading ? 'hidden' : 'visible' }}>
                    <thead>
                        <tr>
                            {selectable && (
                                <th className="bf-table-select-cell">
                                    <input
                                        type="checkbox"
                                        className="bf-table-checkbox"
                                        aria-label="Select all rows"
                                        checked={
                                            processedData.length > 0 &&
                                            processedData.every((record, index) =>
                                                selectionKeys.includes(getRowKey(record, index)),
                                            )
                                        }
                                        onChange={handleSelectAll}
                                    />
                                </th>
                            )}
                            {columns.map((column) => (
                                <SortableHeaderCell
                                    key={column.key}
                                    id={column.key}
                                    draggable={draggable}
                                    className={`bf-table-header-cell ${column.align ? `align-${column.align}` : ''} ${
                                        column.fixed ? `fixed-${column.fixed}` : ''
                                    } ${column.className || ''}`}
                                    style={{
                                        width: column.width,
                                        minWidth: column.minWidth,
                                    }}
                                >
                                    {renderHeaderCell(column)}
                                </SortableHeaderCell>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {processedData.length === 0 ? (
                            <tr className="bf-table-empty-row">
                                <td colSpan={columns.length + (selectable ? 1 : 0)} className="bf-table-empty-cell">
                                    {emptyText}
                                </td>
                            </tr>
                        ) : (
                            processedData.map((record, rowIndex) => {
                                const key = getRowKey(record, rowIndex);
                                // Use a composite render key to avoid duplicate React keys when
                                // data contains repeated logical keys (e.g., duplicated rows).
                                const renderKey = `${String(key)}__${rowIndex}`;
                                const isActive = activeRowKey === key;
                                const isSelected = selectionKeys.includes(key);

                                return (
                                    <tr
                                        key={renderKey}
                                        className={`bf-table-row ${clickable ? 'clickable' : ''} ${
                                            isActive ? 'active' : ''
                                        } ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleRowClick(record, rowIndex)}
                                        onDoubleClick={() => onRowDoubleClick?.(record, rowIndex)}
                                    >
                                        {selectable && (
                                            <td className="bf-table-select-cell">
                                                <input
                                                    type="checkbox"
                                                    className="bf-table-checkbox"
                                                    aria-label="Select row"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectRow(record, rowIndex)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </td>
                                        )}
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className={`bf-table-cell ${
                                                    column.align ? `align-${column.align}` : ''
                                                } ${column.fixed ? `fixed-${column.fixed}` : ''} ${
                                                    column.className || ''
                                                }`}
                                                style={{
                                                    width: column.width,
                                                    minWidth: column.minWidth,
                                                }}
                                            >
                                                {renderCellContent(column, record, rowIndex)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            {currentPagination && (
                <PaginationControls
                    config={currentPagination}
                    onPageChange={(page) => handlePaginationChange(page, currentPagination.pageSize)}
                    onPageSizeChange={(pageSize) => handlePaginationChange(1, pageSize)}
                />
            )}
            {loadMore && (
                <LoadMoreControls config={loadMore} onLoadMore={handleLoadMore} loading={currentLoadMoreLoading} />
            )}
        </div>
    );

    // Wrap with DnD context if draggable
    if (draggable) {
        return (
            <DndContext
                sensors={sensors}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                collisionDetection={closestCenter}
            >
                <SortableContext items={columns.map((col) => col.key)} strategy={horizontalListSortingStrategy}>
                    <DragIndexContext.Provider value={dragIndex}>{tableContent}</DragIndexContext.Provider>
                </SortableContext>
            </DndContext>
        );
    }

    return tableContent;
}
